/**
 * Blocking consent dialog (audit P-4, 2026-10-21). Shown when /me reports
 * consent.required (a new or updated terms version) or a chat turn is refused
 * with terms_required. Accepting records the exact version shown; a version
 * that changed meanwhile asks the app to reload it.
 */
import React, { useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { acceptGuardianTerms } from '../api/guardianApi';
import TermsLinks from './TermsLinks';

export default function TermsConsentDialog({ open, consent, authToken, onAccepted, onSignOut }) {
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const accept = async () => {
    setBusy(true);
    setError(null);
    const res = await acceptGuardianTerms(authToken, consent?.version);
    setBusy(false);
    if (res?.success) {
      setChecked(false);
      onAccepted?.();
      return;
    }
    if (res?.data?.code === 'terms_version_mismatch') {
      setError('The terms were just updated. Review the latest version.');
      onAccepted?.();
      return;
    }
    setError(res?.message || 'Could not record your acceptance. Try again.');
  };

  return (
    <Dialog open={Boolean(open)} aria-labelledby="terms-consent-title" disableEscapeKeyDown>
      <DialogTitle id="terms-consent-title">Review our terms</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          To keep using Guardian, please review and accept our <TermsLinks terms={consent} />.
        </Typography>
        <FormControlLabel
          control={<Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />}
          label={<span>I have read and accept the <TermsLinks terms={consent} /></span>}
        />
        {error && <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onSignOut} color="inherit">Sign out</Button>
        <Button variant="contained" onClick={accept} disabled={!checked || busy || !consent?.version}>
          {busy ? 'Saving…' : 'Accept and continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
